'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { ApiClientError } from '@/shared/lib/api';
import { useLimitErrorPaywall } from '@/shared/hooks/useLimitErrorPaywall';
import { useTokenBalance } from '@/features/subscription/model/subscription.selectors';
import {
  trackEvent,
  AnalyticsEvent,
  BlockReason,
  ErrorType,
} from '@/shared/lib/analytics';
import { toast } from '@/shared/lib/toast';
import { createLogger } from '@/shared/lib/logger';
import type { RenderMode } from '@/types/render';

const logger = createLogger('RenderErrorHandler');

/**
 * The funnel block reason for a render POST the server refused, or null when
 * the refusal is a genuine error rather than a block. Same vocabulary as the
 * preflight gate: typed limits that raced past it count as a block.
 */
export function renderErrorBlockReason(err: unknown): BlockReason | null {
  if (!(err instanceof ApiClientError)) return null;
  if (err.isInsufficientTokensError()) return BlockReason.INSUFFICIENT_TOKENS;
  // Legacy 403 detail OR the typed 422 RENDER_QUEUE_LIMIT_EXCEEDED — both are
  // the same queue saturation in the funnel vocabulary.
  if (err.isQueueLimitError() || err.isRenderQueueLimitError()) {
    return BlockReason.QUEUE_FULL;
  }
  return null;
}

/**
 * What the Generate button does with a refused render POST: analytics, then the
 * server-driven paywall for typed limits, otherwise a toast carrying the API's
 * own message. One handler so a render started anywhere else (the first-run
 * wizard hands its refusal to the concept page) answers exactly like Generate.
 */
export function useRenderErrorHandler(): (
  err: unknown,
  mode: RenderMode
) => void {
  const tCommon = useTranslations('common');
  // Route server-driven paywall errors (INSUFFICIENT_TOKENS, render limits, …)
  // through the shared dialog. When a paywall is shown we skip the local
  // toast fallback so the same error doesn't surface twice.
  const routeToPaywall = useLimitErrorPaywall();
  const tokensBalance = useTokenBalance();

  return useCallback(
    (err: unknown, mode: RenderMode) => {
      const blockReason = renderErrorBlockReason(err);
      if (blockReason) {
        trackEvent(AnalyticsEvent.GENERATE_BLOCKED, {
          reason: blockReason,
          mode,
          tokens_balance: tokensBalance,
        });
      } else {
        trackEvent(AnalyticsEvent.RENDER_ERROR, {
          error_type:
            err instanceof ApiClientError
              ? err.isValidationError()
                ? ErrorType.VALIDATION
                : err.isServerError()
                  ? ErrorType.SERVER
                  : ErrorType.UNKNOWN
              : ErrorType.UNKNOWN,
          mode,
        });
      }

      // API-driven copy: title is a localized generic, description comes
      // from the server. Always a toast: an inline banner at the top of the
      // settings list sits off-screen once submit scrolls the phone layout.
      if (err instanceof ApiClientError) {
        // ALL typed limit errors (insufficient tokens, queue/daily/monthly
        // caps, feature gates) → server-driven paywall. Android parity:
        // every typed limit routes to the paywall presenter. If the route
        // succeeds we suppress the local toast — otherwise (untyped error,
        // or older server with no paywall body) fall back below.
        if (routeToPaywall(err)) {
          return;
        }
        const isWarning = err.isDailyLimitError() || err.isQueueLimitError();
        if (isWarning || err.isInsufficientTokensError()) {
          const description = err.getFirstError() || tCommon('errors.generic');
          const fn = isWarning ? toast.warning : toast.error;
          fn(isWarning ? tCommon('warning') : tCommon('error'), {
            description,
          });
        } else {
          toast.error(tCommon('error'), {
            description: err.getFirstError() || tCommon('errors.generic'),
          });
        }
      } else {
        toast.error(tCommon('error'), {
          description: tCommon('errors.generic'),
        });
        logger.error('Unexpected render creation error:', err);
      }
    },
    [routeToPaywall, tCommon, tokensBalance]
  );
}
